// Status: active
// Messaging-internal Inngest command contracts (bridge/API -> worker).
//
// These commands deliberately contain stable identifiers only. Donor records,
// recipient addresses, rendered HTML, and attachments are loaded and processed
// inside domain use cases so they never become durable Inngest event payloads.

import { z } from 'zod';

export const DONATION_RECEIPT_REQUESTED_EVENT =
  'messaging/email.donation-receipt.requested.v1' as const;
export const SCHEDULED_RECEIPT_REQUESTED_EVENT =
  'messaging/email.scheduled-receipt.requested.v1' as const;
export const GENERIC_NOTICE_REQUESTED_EVENT =
  'messaging/email.generic-notice.requested.v1' as const;
export const EOY_BATCH_REQUESTED_EVENT = 'messaging/email.eoy-batch.requested.v1' as const;
export const EOY_SINGLE_REQUESTED_EVENT = 'messaging/email.eoy-single.requested.v1' as const;
export const APPROVED_EMAIL_DELIVERY_REQUESTED_EVENT =
  'messaging/email.approved-delivery.requested.v1' as const;

/**
 * Exact CharityStack organizations-table primary key.
 *
 * Both migrated organizations (`M_...`) and self-serve/legacy organizations
 * (`org_...`) are valid. Prefix shape does not prove tenant ownership: callers
 * must still resolve Clerk identity through the organizations repository.
 */
export const organizationTableIdSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(
    /^(?:M_|org_)[A-Za-z0-9][A-Za-z0-9_-]*(?![\s\S])/,
    'organizationId must be a valid organizations-table id',
  );

export function isOrganizationTableId(value: unknown): value is string {
  return organizationTableIdSchema.safeParse(value).success;
}

const organizationScopeSchema = z
  .object({
    /** Repository-resolved organizations-table id, never an unverified caller id. */
    organizationId: organizationTableIdSchema,
    merchantName: z.string().min(1).max(512),
  })
  .strict();

/**
 * Queue commands are always tenant-qualified. This prevents one legacy SQS
 * message that spans organizations from being collapsed by Inngest dedupe.
 */
export function messagingQueueCommandId(
  originSqsMessageId: string,
  organizationId: string,
): string {
  return `${originSqsMessageId}:${organizationId}`;
}

/**
 * What `formBrandColor` becomes when the producer sent something that is not
 * even a short string — a `null`, a DynamoDB number, an object, or a value over
 * the length bound.
 *
 * It exists so that "the producer sent nothing" and "the producer sent
 * something unusable" stay distinguishable at the consumer for EVERY input
 * shape, not just for the string-shaped ones. It is never a colour: the
 * consumer refuses it like any other unusable value and logs it, so this string
 * appearing in `MESSAGING_FORM_BRAND_COLOR_UNUSABLE` means "the value was too
 * malformed to carry across the boundary".
 */
export const FORM_BRAND_COLOR_UNPARSEABLE = '<unparseable>' as const;

/**
 * A donation form's own brand colour (the forms table's `lightColor`), for the
 * donor-facing mail this command produces.
 *
 * Three properties, each deliberate (`decisions.md` **D8**):
 *
 * **Optional.** Event-driven paths deliver at-least-once and there may be
 * commands already queued when a deploy lands. A required field would reject
 * every one of them — on the transactional receipt path of a live payment
 * product. The consumer falls back to the organisation-level
 * `brandAssets.primaryColor`, which is what every receipt uses today, so an
 * absent field renders exactly as it renders now.
 *
 * **Nothing here rejects, and nothing here drops SILENTLY.** Both halves are
 * load-bearing and the second one is easy to lose.
 *
 * Rejecting is out because a presentational field must never be the reason a
 * donor's receipt fails to send. But a non-failing shape check is a SILENT
 * FILTER: a first revision used `.regex(...).optional().catch(undefined)`, so a
 * colour stored as `rgb(0,0,0)` vanished at the parse boundary with no log, and
 * the feature would have been dead for that organisation with no signal
 * anywhere. Narrowing that filter to length-and-type is not enough either — a
 * `null`, a DynamoDB number, or a 65-character value still becomes
 * indistinguishable from "the producer sent nothing".
 *
 * So: an **absent** field stays absent, and **every other** unusable input
 * arrives at the consumer as something it can report.
 *
 *   | input                                   | parses to        | consumer     |
 *   |-----------------------------------------|------------------|--------------|
 *   | key absent, or `undefined`              | `undefined`      | silent — the normal case, and every command queued before this field existed |
 *   | `'#2A6EA5'`                             | itself           | used         |
 *   | `'rgb(0,0,0)'`, `'teal'`, `''`, `'  '`  | itself           | refused, and the VALUE is logged |
 *   | `null`, a number, an object, > 64 chars | `FORM_BRAND_COLOR_UNPARSEABLE` | refused, and the marker is logged |
 *
 * Validation itself lives at the one consumer chokepoint
 * (`resolveDonorBrandColor` in `backends/messaging`), which falls back to the
 * organisation colour and hands the caller the value it could not use.
 *
 * **Carried on the command rather than read at render time.** The receipt
 * occurrence hash covers the rendered presentation, so a value that could
 * change between retries of the same occurrence would surface as an outbox
 * conflict. A value fixed on the command is stable across retries by
 * construction.
 *
 * Expected shape, for producers: a 3- or 6-digit hex colour with a leading `#`.
 */
const formBrandColorSchema = z
  .string()
  .max(64)
  .optional()
  .catch(FORM_BRAND_COLOR_UNPARSEABLE);

const queueOriginSchema = organizationScopeSchema.extend({
  /** Original queue identifier retained for operations/audit correlation. */
  originSqsMessageId: z.string().min(1).max(256),
  /** Tenant-qualified value used as the Inngest event/delivery id. */
  originIdempotencyKey: z.string().min(1).max(520),
  /**
   * Declared here, on the queue-origin base, so every donor-facing queue
   * command spells it the same way. The end-of-year schemas extend
   * `organizationScopeSchema` instead and deliberately do NOT get it: an EOY
   * statement summarises a year of donations across many forms, so the
   * organisation colour is the correct identity there.
   */
  formBrandColor: formBrandColorSchema,
});

function hasTenantQualifiedQueueId(value: z.infer<typeof queueOriginSchema>): boolean {
  return (
    value.originIdempotencyKey ===
    messagingQueueCommandId(value.originSqsMessageId, value.organizationId)
  );
}

const tenantQualifiedQueueIdError: {
  message: string;
  path: Array<string | number>;
} = {
  message: 'originIdempotencyKey must match originSqsMessageId and organizationId',
  path: ['originIdempotencyKey'],
};

export const donationReceiptRequestedSchema = queueOriginSchema
  .extend({
    transactionId: z.string().min(1).max(256),
    notifyMerchant: z.boolean(),
    /**
     * Bridge-owned occurrence semantics. Optional only so events accepted
     * before this field deployed retain the historical payment-success
     * identity instead of being reclassified during an in-flight retry.
     */
    requestKind: z.enum(['PAYMENT_SUCCESS', 'MANUAL_RESEND']).optional(),
  })
  .refine(hasTenantQualifiedQueueId, tenantQualifiedQueueIdError);
export type DonationReceiptRequested = z.infer<typeof donationReceiptRequestedSchema>;

export const scheduledReceiptRequestedSchema = queueOriginSchema
  .extend({
    subscriptionId: z.string().min(1).max(256),
    /** Successful charge captured before the mutable subscription row advances. */
    chargeTransactionId: z.string().min(1).max(256),
  })
  .refine(hasTenantQualifiedQueueId, tenantQualifiedQueueIdError);
export type ScheduledReceiptRequested = z.infer<typeof scheduledReceiptRequestedSchema>;

export const GENERIC_MESSAGE_TYPES = [
  'FAIL_RECEIPT',
  'ACH_TRANSFER_PENDING',
  'REFUND_INITIATED',
  'UPDATED_PAYMENT_INSTRUMENT',
  'CANCELED_DONOR_SUBSCRIPTION',
  'REACTIVATED_DONOR_SUBSCRIPTION',
  'UPDATED_DONOR_SUBSCRIPTION',
] as const;
export type GenericMessageType = (typeof GENERIC_MESSAGE_TYPES)[number];

const transactionNoticeSchema = queueOriginSchema.extend({
  messageType: z.enum(['FAIL_RECEIPT', 'ACH_TRANSFER_PENDING']),
  transactionId: z.string().min(1).max(256),
});

const refundNoticeSchema = queueOriginSchema.extend({
  messageType: z.literal('REFUND_INITIATED'),
  transactionId: z.string().min(1).max(256),
  refundAmount: z.union([
    z.number().finite().nonnegative(),
    z
      .string()
      .max(128)
      .regex(/^\d+(?:\.\d+)?$/, 'refundAmount must be nonnegative'),
  ]),
});

const subscriptionIdsSchema = z
  .array(z.string().min(1).max(256))
  .min(1)
  .max(1_000)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'reoccurringDonorIds must be unique',
  });

const paymentInstrumentNoticeSchema = queueOriginSchema.extend({
  messageType: z.literal('UPDATED_PAYMENT_INSTRUMENT'),
  reoccurringDonorIds: subscriptionIdsSchema,
  /** True for exactly one org when a legacy payload spans multiple orgs. */
  sendDonorCopy: z.boolean(),
  /** Exact legacy base subscription used for donor branding/config, or null for merchant-only groups. */
  donorSubscriptionId: z.string().min(1).max(256).nullable(),
  /** False for the inactive fallback group when no subscription is active. */
  sendMerchantCopy: z.boolean(),
});

const singleSubscriptionIdSchema = z
  .array(z.string().min(1).max(256))
  .length(1, 'lifecycle notices target exactly one subscription');

const subscriptionNoticeSchema = queueOriginSchema.extend({
  messageType: z.enum([
    'CANCELED_DONOR_SUBSCRIPTION',
    'REACTIVATED_DONOR_SUBSCRIPTION',
    'UPDATED_DONOR_SUBSCRIPTION',
  ]),
  reoccurringDonorIds: singleSubscriptionIdSchema,
});

export const genericNoticeRequestedSchema = z
  .discriminatedUnion('messageType', [
    transactionNoticeSchema,
    refundNoticeSchema,
    paymentInstrumentNoticeSchema,
    subscriptionNoticeSchema,
  ])
  .refine(hasTenantQualifiedQueueId, tenantQualifiedQueueIdError)
  .superRefine((command, context) => {
    if (command.messageType !== 'UPDATED_PAYMENT_INSTRUMENT') return;
    if (command.sendDonorCopy) {
      if (
        command.donorSubscriptionId === null ||
        !command.reoccurringDonorIds.includes(command.donorSubscriptionId)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'donorSubscriptionId must identify a subscription in the donor-copy command',
          path: ['donorSubscriptionId'],
        });
      }
    } else if (command.donorSubscriptionId !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'merchant-only commands must not select a donor subscription',
        path: ['donorSubscriptionId'],
      });
    }
  });
export type GenericNoticeRequested = z.infer<typeof genericNoticeRequestedSchema>;

const eoyBaseSchema = organizationScopeSchema.extend({
  year: z.number().int().min(2000).max(9999),
  merchantTimezone: z.string().min(1).max(128).optional(),
  /** CON-754: an ISO 4217 code keeps only that currency's rows; absent means ALL. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .refine((code) => code !== 'ALL', { message: 'ALL is expressed by omitting currency' })
    .optional(),
  /** HTTP/invoke idempotency key; distinct from donor/contact identity. */
  originIdempotencyKey: z.string().min(1).max(256),
});

export const eoySingleRequestedSchema = eoyBaseSchema.extend({
  contactId: z.string().min(1).max(256),
});
export type EoySingleRequested = z.infer<typeof eoySingleRequestedSchema>;

export const eoyBatchRequestedSchema = eoyBaseSchema.extend({
  contactIds: z
    .array(z.string().min(1).max(256))
    .min(1)
    .max(1_000)
    .refine((ids) => new Set(ids).size === ids.length, {
      message: 'contactIds must be unique',
    }),
  requestedBy: z.string().min(1).max(256),
});
export type EoyBatchRequested = z.infer<typeof eoyBatchRequestedSchema>;

export const approvedEmailDeliveryRequestedSchema = z
  .object({
    organizationId: organizationScopeSchema.shape.organizationId,
    draftId: z.string().min(8).max(256),
    snapshotHash: z.string().regex(/^[a-f0-9]{64}$/),
    deliveryRequestId: z.string().min(8).max(256),
  })
  .strict();
export type ApprovedEmailDeliveryRequested = z.infer<typeof approvedEmailDeliveryRequestedSchema>;
